import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '@ajat/design-system/Header';
import SchoolSelector from './SchoolSelector';
import EditOrderLogin from '@ajat/design-system/EditOrderLogin';
import { useCart } from '../../../../context/CartContext';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useBrandSeo from '../../../../hooks/useBrandSeo';
import useDocumentLang from '../../../../hooks/useDocumentLang';
import useHeaderMenuIntent from '../../../../hooks/useHeaderMenuIntent';
import useMenuItemVisibility from '../../../../hooks/useMenuItemVisibility';
import { readFreshSelection } from '../../../../services/schoolSelectionTtl';
import { loginToOrder } from '../../Common/EditOrder/ordersApi';

/* ══════════════════════════════════════════════════════════════════════════
 * ABC-SE — Common Header
 * Single source of truth for header configuration across all ABC-SE pages
 * ══════════════════════════════════════════════════════════════════════════ */

const buildMobileSettings = (onMenuItemClick, showMenu1Promo = true) => ({
  mobileTitle: 'Meny',
  // The promo card is tied to menu_1 — hide it when the item itself is hidden
  // (open shop disabled + no menu_1 shop for the customer's school).
  ...(showMenu1Promo ? {
    mobilePromo: {
      cardType: 'button',
      image: require('../../../../assets/abc-se/menu/menu-item-9.jpg'),
      imageAlt: 'Buy skolmerch',
      label: 'KÖP SKOLMERCH',
      href: '#',
      onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
    },
  } : {}),
});

// Built per instance so "Skolmerch" (menu_1) and "Overalls & More" (menu_2)
// resolve to the customer's mapped school shop via onMenuItemClick.
// Flat, non-expanding nav — other menu items and mega-menus hidden for now.
// `menuKey` tags the two fixed entries so the header can hide one under the
// "Disable Open Shop" flag (useMenuItemVisibility); untagged entries stay.
const buildNavItems = (onMenuItemClick) => [
  {
    label: 'Skolmerch',
    menuKey: 'menu_1',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_1'); },
  },
  {
    label: 'Overalls & More',
    menuKey: 'menu_2',
    href: '#',
    onClick: (e) => { e.preventDefault(); onMenuItemClick('menu_2'); },
  },
  {
    label: 'Kundservice',
    href: '/clothing/abc-se/faq',
  },
  {
    label: 'Studentmössor',
    href: 'https://www.abcgruppen.se/',
  },
];

const ABCSEHeader = ({ hideOnScroll = true, onSchoolClick, ...props }) => {
  const navigate = useNavigate();
  // Sibling routes that share one route element (e.g. /shop/:menuSlug) keep
  // this Header mounted, so the DS component needs a navigation signal to
  // close its open menus. location.key changes on EVERY history entry —
  // including a menu click that re-navigates to the page you are already on.
  const location = useLocation();
  const cart = useCart('abcse');
  const { showSchoolSelector } = useBrandSettings('abcse');
  useBrandSeo('abcse');
  useDocumentLang('sv');   // <html lang> — public/index.html says "da"
  const [isSchoolSelectorOpen, setIsSchoolSelectorOpen] = useState(false);
  const [isEditOrderLoginOpen, setIsEditOrderLoginOpen] = useState(false);
  const [pendingNavigate, setPendingNavigate] = useState(null);

  // Menu-item → mapped-shop resolution for the two fixed header items. When
  // the selector is disabled for the brand the hook navigates straight to the
  // menu item's fallback listing page (it never dispatches the popup event).
  const {
    onMenuItemClick,
    hasMenuIntent,
    readIntentFromEvent,
    clearIntent,
    preferredSeasonResolver,
    navigateForIntentAfterSelect,
  } = useHeaderMenuIntent({ brandSlug: 'abcse', feSlug: 'abc-se' });

  // Hide a menu item when the open shop is disabled and the customer's school
  // has no open shop of that type (untagged entries always stay).
  const { isVisible } = useMenuItemVisibility('abcse');
  const navItems = useMemo(
    () => buildNavItems(onMenuItemClick).filter((i) => !i.menuKey || isVisible(i.menuKey)),
    [onMenuItemClick, isVisible],
  );
  const mobileSettings = useMemo(
    () => buildMobileSettings(onMenuItemClick, isVisible('menu_1')),
    [onMenuItemClick, isVisible],
  );

  // Show the selected school's name on the header school button (read fresh
  // each render — selecting/clearing re-renders via the selector state).
  const storedSchoolName = readFreshSelection('abcse_school_selection')?.school?.name || null;

  /* Build minicart items from cart context */
  const priceSuffix = ':-';
  const cartItems = [...cart.products, ...cart.packages].map((item) => {
    const opts = item.options || {};
    const details = [];
    if (opts.color) details.push(opts.color + (opts.size ? ` ${opts.size}` : ''));
    else if (opts.size) details.push(opts.size);
    if (opts.addons?.namn) details.push(`Namn: ${opts.addons.namn}`);
    // Bonus products (server-appended, free) stay out of the minicart count.
    const visibleIncluded = (item.includedProducts || []).filter((ip) => !ip?.bonus);
    if (visibleIncluded.length) details.push(`${visibleIncluded.length} produkter inkluderade`);
    return {
      id: item.id,
      name: item.name,
      image: opts.image || item.image,
      details,
      quantity: item.quantity,
      price: `${Math.round(item.lineTotal)}${priceSuffix}`,
    };
  });

  // ABC-brand favicon (copied from www.abcgruppen.se): swap every icon link
  // while an ABC page is mounted; restore the defaults on unmount so the
  // other brands / dev project picker keep the CLS icons.
  useEffect(() => {
    const links = Array.from(document.querySelectorAll("link[rel*='icon']"));
    const saved = links.map((l) => ({
      l, href: l.getAttribute('href'), type: l.getAttribute('type'),
    }));
    const base = process.env.PUBLIC_URL || '';
    links.forEach((l) => {
      const sizePx = parseInt(l.getAttribute('sizes') || '0', 10);
      const wantBig = l.rel.includes('apple') || sizePx >= 96;
      l.setAttribute('href', `${base}/abc-favicon-${wantBig ? '192' : '32'}.png`);
      if (l.getAttribute('type')) l.setAttribute('type', 'image/png');
    });
    return () => saved.forEach(({ l, href, type }) => {
      if (href) l.setAttribute('href', href);
      if (type) l.setAttribute('type', type);
    });
  }, []);

  useEffect(() => {
    if (!showSchoolSelector) return undefined;
    const handleOpen = (e) => {
      setIsSchoolSelectorOpen(true);
      setPendingNavigate(e.detail?.navigateTo ?? null);
      readIntentFromEvent(e);
    };
    document.addEventListener('open-school-selector', handleOpen);
    return () => {
      document.removeEventListener('open-school-selector', handleOpen);
    };
  }, [showSchoolSelector, readIntentFromEvent]);

  const handleSchoolClick = () => {
    if (!showSchoolSelector) return;
    setIsSchoolSelectorOpen(true);
    setPendingNavigate(null);
    clearIntent();
    if (onSchoolClick) onSchoolClick();
  };

  const handleUserClick = () => {
    setIsEditOrderLoginOpen(true);
  };

  return (
    <>
      <Header
        brand="ABC"
        subBrand="main"
        mode="default"
        onLogoClick={(e) => { e.preventDefault(); navigate('/clothing/abc-se'); }}
        schoolLabel={storedSchoolName || undefined}
        hideOnScroll={hideOnScroll}
        navItems={navItems}
        mobileSettings={mobileSettings}
        routeKey={location.key}
        onSchoolClick={handleSchoolClick}
        showSchoolButton={showSchoolSelector}
        cartCount={cart.itemCount}
        cartItems={cartItems}
        cartTotal={`${Math.round(cart.total)}:-`}
        cartItemsLabel="Varor"
        cartTotalLabel="Totalt"
        cartCtaLabel="Till kassan"
        onCartCtaClick={() => navigate('/clothing/abc-se/shopping-cart')}
        showUserButton
        orderLabel="Ändra beställning"
        onUserClick={handleUserClick}
        {...props}
      />
      <SchoolSelector
        isOpen={showSchoolSelector && isSchoolSelectorOpen}
        suppressNavigate={hasMenuIntent}
        preferredSeasonResolver={preferredSeasonResolver}
        onClose={() => {
          setIsSchoolSelectorOpen(false);
          setPendingNavigate(null);
          // Dismissing just closes the popup; drop the pending menu intent.
          clearIntent();
        }}
        onSelect={(selection) => {
          setIsSchoolSelectorOpen(false);
          // A pending menu intent navigates to the mapped shop (or fallback).
          if (navigateForIntentAfterSelect(selection)) return;
          if (pendingNavigate) {
            const target = pendingNavigate;
            setPendingNavigate(null);
            navigate(target);
          }
        }}
        onNotFoundClick={() => {
          setPendingNavigate(null);
          navigate('/clothing/abc-se/contact');
        }}
      />
      <EditOrderLogin
        onLogin={loginToOrder}
        isOpen={isEditOrderLoginOpen}
        onClose={() => setIsEditOrderLoginOpen(false)}
        apiBrandSlug="abcse"
        onSuccess={({ orderNumber, token, order }) => {
          setIsEditOrderLoginOpen(false);
          navigate(`/clothing/abc-se/edit-order/${orderNumber}`, { state: { token, order } });
        }}
        notFoundMessage="Vi kunde inte hitta någon beställning med den e-postadressen och det ordernumret."
        title="Ändra beställning"
        infoText="Du hittar ditt ordernummer i bekräftelsemejlet vi har skickat till dig."
        emailLabel="E-post"
        orderNumberLabel="Ordernummer"
        submitLabel="Fortsätt"
        emailErrorMessage="Ange en giltig e-postadress"
        orderNumberErrorMessage="Ange ett giltigt ordernummer"
      />
    </>
  );
};

export default ABCSEHeader;
